import { useSignIn } from "@clerk/clerk-react";
import { useCallback, useRef, useState } from "react";
import { impersonateAdminCampaign } from "@/api/adminCampaigns";
import { CAMPAIGN_BUILDER_ID_KEY } from "@/constants/campaignBuilder";
import { useStartImpersonation } from "@/hooks/useUsers";

/** The part of a Clerk session this module touches (avoids depending on @clerk/types directly). */
export type ReplaceableSession = { actor?: unknown; remove: () => Promise<unknown> };

/**
 * Sign a Clerk actor token in ON THIS ORIGIN.
 *
 * `POST /admin/users/impersonation/start` returns the actor token as both a `url` and a raw
 * `token`. The `url` is the Clerk Account Portal for the instance: it signs the ticket in there
 * and then redirects to the instance's configured home URL — staging, for the shared dev
 * instance — so following it from localhost or a PR preview always lands on staging. The raw
 * `token` is a sign-in ticket, so it is consumed right here with the ticket strategy (the same
 * `signIn.create` / `setActive` pair `useSignInFlow` uses for the email code) and the browser
 * stays wherever it already is.
 *
 * The admin's own session is kept: the instance runs multi-session, which is what
 * `ImpersonationBanner`'s Stop relies on to switch back without a fresh sign-in. The new session
 * carries `session.actor`, so the banner and `useAuth().actor` behave exactly as before. A full
 * navigation to "/" follows so every cache (React Query, permissions) restarts as the
 * impersonated user; `LandingRedirect` then routes them to their own home.
 */
export function useActivateActorToken() {
  const { isLoaded, signIn, setActive } = useSignIn();

  const activate = useCallback(
    async (
      ticket: string,
      opts?: { replaceSession?: ReplaceableSession | null; redirectTo?: string },
    ) => {
      if (!isLoaded || !signIn || !setActive) throw new Error("Clerk is not ready yet.");
      const attempt = await signIn.create({ strategy: "ticket", ticket });
      if (attempt.status !== "complete" || !attempt.createdSessionId) {
        throw new Error(`Impersonation sign-in did not complete (status: ${attempt.status}).`);
      }
      // Switching from one impersonation straight to another: drop the old actor session so they
      // don't pile up in the session list the banner searches for the admin's own.
      if (opts?.replaceSession?.actor) {
        await opts.replaceSession.remove().catch((e: unknown) => {
          console.error("Failed to remove previous impersonation session", e);
        });
      }
      await setActive({ session: attempt.createdSessionId });
      window.location.assign(opts?.redirectTo ?? "/");
    },
    [isLoaded, signIn, setActive],
  );

  return { activate, isLoaded };
}

/**
 * Open a campaign in the Campaign Builder as its creator (GFG-358): the API issues the actor token
 * for the creator, the campaign id is stashed for the builder provider (same key My Campaigns uses
 * to open a campaign), and the sign-in lands on the builder's first stage.
 */
export function useImpersonateCampaign() {
  const { activate } = useActivateActorToken();
  const [isPending, setPending] = useState(false);
  // A ref, not the state: a double-click lands both calls before the re-render that sets isPending.
  const inFlight = useRef(false);

  const impersonateCampaign = useCallback(
    async (campaignId: number) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setPending(true);
      try {
        const { actorToken } = await impersonateAdminCampaign(campaignId);
        sessionStorage.setItem(CAMPAIGN_BUILDER_ID_KEY, String(campaignId));
        await activate(actorToken.token, { redirectTo: "/campaign/create" });
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [activate],
  );

  return { impersonateCampaign, isPending };
}

/**
 * Start impersonating a user from an ADMIN session: request the actor token with the current
 * session's credentials, then activate it here. (`SwitchUserModal` runs while already
 * impersonating, so it fetches the token with the admin session's token and calls
 * `useActivateActorToken` directly.)
 */
export function useImpersonate() {
  const start = useStartImpersonation();
  const { activate } = useActivateActorToken();
  const [isActivating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const impersonate = useCallback(
    async (targetClerkUserId: string) => {
      setError(null);
      setActivating(true);
      try {
        const res = await start.mutateAsync({ targetUserId: targetClerkUserId });
        const ticket = res.actorToken?.token;
        if (!ticket) throw new Error(res.error ?? "Clerk returned no actor token.");
        await activate(ticket);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Impersonation failed.";
        console.error("useImpersonate:", e);
        setError(message);
        setActivating(false);
      }
    },
    [start, activate],
  );

  return { impersonate, isPending: start.isPending || isActivating, error };
}
